import { useCallback, type ReactNode } from 'react';
import { Button, ButtonProps } from '@antimatterhq/ui/components/button';
import { cn } from '@antimatterhq/ui/lib/utils';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';

type Option = {
  label: ReactNode;
  value: string;
  variant?: ButtonProps['variant'];
  info?: {
    content: ReactNode;
  };
};

export type ButtonGroupProps = {
  options: Option[];
  value?: string;
  onChange: (value: string | undefined) => void;
  name?: string;
  disabled?: boolean;
  className?: string;
  equalWidth?: boolean;
  'data-testid'?: string;
  id?: string;
  buttonClassName?: string;
};

type ButtonGroupButtonProps = {
  option: Option;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  equalWidth: boolean;
  onChange: (value: string | undefined) => void;
  name: string;
  disabled: boolean;
  dataTestId: string;
  id: string;
  className?: string;
};

const ButtonGroupButton = ({
  option,
  isSelected,
  isFirst,
  isLast,
  equalWidth,
  onChange,
  name,
  disabled,
  dataTestId,
  id,
  className,
}: ButtonGroupButtonProps) => {
  const onClick = useCallback(() => {
    if (isSelected) return;
    onChange(option.value);
  }, [isSelected, onChange, option.value]);

  return (
    <Button
      key={option.value}
      className={cn(
        'relative -ml-[1px] min-w-[100px] rounded-none first:ml-0 last:border-r',
        'border border-grey-200 hover:z-[1] hover:border hover:border-blue-700 hover:bg-[#F0F8FF] hover:text-blue-700 dark:hover:border dark:hover:border-blue-400 dark:hover:bg-[#04355D80] dark:hover:text-blue-400',
        isSelected &&
          'z-[2] border border-blue-800 bg-[#D7EDFE] hover:border hover:border-blue-700 hover:text-blue-700 dark:border-blue-200 dark:bg-[#BAE1FC33] dark:text-blue-200 dark:hover:border dark:hover:border-blue-400 dark:hover:bg-[#04355D80] dark:hover:text-blue-400',
        isFirst && 'rounded-l-md',
        isLast && 'rounded-r-md',
        equalWidth && 'flex-1',
        'text-xs',
        className,
      )}
      onClick={onClick}
      variant={option.variant}
      name={name}
      value={option.value}
      type="button"
      disabled={disabled}
      data-selected={isSelected}
      data-testid={`${dataTestId}-button-${option.value}`}
      id={id ? `${id}-button-${option.value}` : undefined}
    >
      {option.label}
    </Button>
  );
};

export function ButtonGroup({
  options,
  value,
  onChange,
  name,
  disabled,
  className,
  equalWidth = false,
  'data-testid': dataTestId = 'button-group',
  id,
  buttonClassName,
}: ButtonGroupProps) {
  return (
    <div
      className={cn('inline-flex', className)}
      data-testid={dataTestId}
      id={id}
    >
      {options.map((option, index) => {
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isSelected = option.value === value;

        const button = (
          <ButtonGroupButton
            key={`${option.value}-${index}`}
            option={option}
            isSelected={isSelected}
            isFirst={isFirst}
            isLast={isLast}
            equalWidth={equalWidth}
            onChange={onChange}
            name={name ?? ''}
            disabled={disabled ?? false}
            dataTestId={dataTestId ?? ''}
            id={id ?? ''}
            className={buttonClassName}
          />
        );

        return option.info ? (
          <InfoTooltip
            key={option.value}
            content={option.info.content}
            trigger={button}
            side="top"
            align="start"
          />
        ) : (
          button
        );
      })}
    </div>
  );
}
